import { fetchWithAuth, fetchWithOptionalAuth } from '@/lib/authenticated-fetch';

export type CargoBranch = {
  id: string;
  code: string;
  name: string;
  city: string;
  district?: string | null;
  khoroo?: string | null;
  addressLine1: string;
  addressLine2?: string | null;
  latitude: number | string;
  longitude: number | string;
  mapLabel?: string | null;
  imageUrl?: string | null;
  phone?: string | null;
  email?: string | null;
  openingHours?: Record<string, string> | null;
  pickupInstructions?: string | null;
  landmark?: string | null;
  coverageNotes?: string | null;
  sortOrder?: number;
};

export type DeliveryOrder = {
  id: string;
  total: number | string;
  status: string;
  paymentStatus: string;
  fulfillmentStatus?: string;
  deliverySelectionStatus?: string;
  paymentDate?: string | null;
  createdAt: string;
  shippingAddress?: string | Record<string, unknown> | null;
  cargoBranchId?: string | null;
  cargoBranch?: CargoBranch | null;
  items?: unknown;
};

export async function fetchCargoBranches(search?: string): Promise<CargoBranch[]> {
  const query = search ? `?search=${encodeURIComponent(search)}` : '';
  const response = await fetchWithOptionalAuth(`${import.meta.env.VITE_API_URL}/api/cargo/branches${query}`);

  if (!response.ok) {
    throw new Error('Failed to fetch cargo branches');
  }

  const data = await response.json();
  return data.branches;
}

export async function fetchDeliveryOrder(orderId: string): Promise<DeliveryOrder> {
  const response = await fetchWithAuth(`${import.meta.env.VITE_API_URL}/api/orders/${orderId}`);

  if (!response.ok) {
    throw new Error('Failed to fetch order');
  }

  const data = await response.json();
  return data.order;
}

export async function saveDeliverySelection(orderId: string, cargoBranchId: string) {
  const response = await fetchWithAuth(`${import.meta.env.VITE_API_URL}/api/orders/${orderId}/delivery-selection`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ cargoBranchId }),
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data?.error || 'Failed to save delivery selection');
  }

  return data as { order: DeliveryOrder; idempotent: boolean };
}
